import { logError } from '@databases/logs';
import { type ValueToken } from '@databases/streaming-csv-parser';
import { type UserReference } from '@databases/types/confluence';
import {
  type DatabaseField,
  DatabaseFieldType,
  type DatabaseValue,
  type Hyperlink,
} from '@databases/types/database';
import { getContentById } from '@databases/utils/confluence/content';
import { getInitialValue } from '@databases/utils/database/field-types';
import { matchViewPageOrBlogpostPath } from '@databases/utils/urls';
import { generateId } from '@databases/utils/utils';

import { JiraService } from '@canvas/atlassian-service/jira';

import { type DatabaseAgent } from './agents/database-agent';
import { JiraIssueDataLoader } from './data-loaders/jira-issue-data-loader';
import { UserDataLoader } from './data-loaders/user-data-loader';
import type { DatabaseValueChange, ProcessingError } from './types';
import { extractIssueKeyFromUrl, getCloudId } from './utils';

export class ValueProcessor {
  private agent: DatabaseAgent;
  private currentEntryId: string | undefined;
  private currentFieldId: string | undefined;
  private userDataLoader: UserDataLoader;
  private jiraIssueDataLoaders: Map<string, JiraIssueDataLoader>;
  private onCurrentEntryIdChange: ((entryId: string | undefined) => void) | undefined;
  private onCurrentFieldIdChange: ((fieldId: string | undefined) => void) | undefined;
  private onValueProcessed: ((fieldId: string, entryId: string) => void) | undefined =
    () => {};

  constructor(
    agent: DatabaseAgent,
    onCurrentEntryIdChange?: (entryId: string | undefined) => void,
    onCurrentFieldIdChange?: (fieldId: string | undefined) => void,
    onValueProcessed?: (fieldId: string, entryId: string) => void
  ) {
    this.agent = agent;
    this.userDataLoader = new UserDataLoader();
    this.jiraIssueDataLoaders = new Map();
    this.onCurrentEntryIdChange = onCurrentEntryIdChange;
    this.onCurrentFieldIdChange = onCurrentFieldIdChange;
    this.onValueProcessed = onValueProcessed;
  }

  public setCurrentEntryId(entryId: string | undefined): void {
    this.clearCurrentFieldId();
    this.currentEntryId = entryId;
    if (this.onCurrentEntryIdChange) {
      this.onCurrentEntryIdChange(entryId);
    }
  }

  public setCurrentFieldId(fieldId: string | undefined): void {
    this.currentFieldId = fieldId;
    if (this.onCurrentFieldIdChange) {
      this.onCurrentFieldIdChange(fieldId);
    }
  }

  public clearCurrentEntryId(): void {
    this.setCurrentEntryId(undefined);
  }

  public clearCurrentFieldId(): void {
    this.setCurrentFieldId(undefined);
  }

  public getCurrentEntryId(): string | undefined {
    return this.currentEntryId;
  }

  public getCurrentFieldId(): string | undefined {
    return this.currentFieldId;
  }

  private static isDatabaseValueChange(
    result: DatabaseValue | DatabaseValueChange
  ): result is DatabaseValueChange {
    return 'databaseValue' in result && 'entryIdToChange' in result;
  }

  public processValueToken(valueToken: ValueToken): void {
    if (!this.currentEntryId) {
      this.logValidationErrors([
        {
          message: 'No current entry ID set. Cannot process value token.',
        },
      ]);
      return;
    }

    const validationResult = this.validateValue(valueToken);

    if (validationResult.errors.length === 0) {
      const { fieldId, field } = validationResult;

      // these values should always exist if validation passes. This is for typescript.
      if (!fieldId || !field) {
        return;
      }

      if (valueToken.value === null) {
        return;
      }

      if (valueToken.value === 'NULL') {
        this.agent.setValue(this.currentEntryId, fieldId, '', true);
        if (this.onValueProcessed) {
          this.onValueProcessed(fieldId, this.currentEntryId);
        }
        return;
      }

      this.setCurrentFieldId(fieldId);
      this.processValueByFieldType(field, fieldId, valueToken.value)
        .then((result) => {
          let entryId = this.currentEntryId;
          if (ValueProcessor.isDatabaseValueChange(result)) {
            entryId = result.entryIdToChange;
            this.agent.setValue(
              entryId,
              fieldId,
              result.databaseValue,
              valueToken.resetValue
            );
          } else if (this.currentEntryId) {
            entryId = this.currentEntryId;
            this.agent.setValue(entryId, fieldId, result, valueToken.resetValue);
          }
          if (this.onValueProcessed && entryId) {
            this.onValueProcessed(fieldId, entryId);
          }
        })
        .catch((error) => {
          this.logValidationErrors([
            {
              message: `Failed to set value for field ${valueToken.fieldName}: ${error}`,
            },
          ]);
        });
    } else {
      this.logValidationErrors(validationResult.errors);
    }
  }

  private validateValue(valueToken: ValueToken): {
    errors: ProcessingError[];
    fieldId?: string;
    field?: DatabaseField;
  } {
    const errors: ProcessingError[] = [];

    if (!valueToken.fieldName) {
      errors.push({
        message: 'Field name is missing',
      });
      return { errors };
    }

    if (valueToken.value === undefined) {
      errors.push({
        message: `Value is missing for field ${valueToken.fieldName}`,
      });
    }

    if (!this.currentEntryId) {
      errors.push({
        message: 'No current entry to set value for',
      });
    }

    const fieldId = this.agent.getFieldIdByName(valueToken.fieldName);
    if (!fieldId) {
      errors.push({
        message: `Field ${valueToken.fieldName} not found`,
      });
      return { errors };
    }

    const field = this.agent.getFieldById(fieldId);
    if (!field) {
      errors.push({
        message: `Field with ID ${fieldId} not found`,
      });
      return { errors };
    }

    return { errors, fieldId, field };
  }

  public async processValueByFieldType(
    field: DatabaseField,
    fieldId: string,
    value: string
  ): Promise<DatabaseValue | DatabaseValueChange> {
    return await this.convertValueByFieldType(this.agent, field, fieldId, value);
  }

  public async convertValueByFieldType(
    agent: DatabaseAgent,
    field: DatabaseField,
    fieldId: string,
    value: string,
    isFilter?: boolean
  ): Promise<DatabaseValue | DatabaseValueChange> {
    switch (field.type) {
      case DatabaseFieldType.TEXT:
        return ValueProcessor.processTextValue(value);

      case DatabaseFieldType.NUMBER:
        return ValueProcessor.processNumberValue(value);

      case DatabaseFieldType.DATE:
        return ValueProcessor.processDateValue(value);

      case DatabaseFieldType.SELECT:
        return ValueProcessor.processSelectValue(agent, fieldId, value);

      case DatabaseFieldType.HYPERLINK:
        return ValueProcessor.processHyperlinkValue(value);

      case DatabaseFieldType.USER:
        if (isFilter) {
          return await this.processUserValueForFilter(
            value.split(',').map((v) => v.trim())
          );
        }

        if (this.currentEntryId) {
          return await this.processUserValue(
            value.split(',').map((v) => v.trim()),
            this.currentEntryId
          );
        }
        return getInitialValue(field.type);

      case DatabaseFieldType.JIRA_ISSUE:
        if (this.currentEntryId) {
          return await this.processJiraIssueValue(
            fieldId,
            value.split(',').map((v) => v.trim()),
            this.currentEntryId
          );
        }
        return getInitialValue(field.type);

      case DatabaseFieldType.PAGE_LINK:
        if (this.currentEntryId) {
          return this.processPageLinkValue(value, this.currentEntryId);
        }
        return getInitialValue(field.type);

      case DatabaseFieldType.ATTACHMENT:
      case DatabaseFieldType.CHECKBOX:
      case DatabaseFieldType.PAGE_LABELS:
      case DatabaseFieldType.PAGE_STATUS:
      case DatabaseFieldType.PAGE_DETAILS:
      case DatabaseFieldType.JIRA_ISSUE_DETAILS:
      case DatabaseFieldType.ENTRY_LINK:
      case DatabaseFieldType.ENTRY_DETAILS:
      case DatabaseFieldType.BACKLINK:
        return getInitialValue(field.type);

      default:
        throw new Error(`Unsupported field type for value processing: ${field.type}`);
    }
  }

  private static processTextValue(value: string): DatabaseValue {
    return {
      type: DatabaseFieldType.TEXT,
      value: value,
    };
  }

  private static processNumberValue(value: string): DatabaseValue {
    return {
      type: DatabaseFieldType.NUMBER,
      value: String(value),
    };
  }

  private static processDateValue(value: string): DatabaseValue {
    return {
      type: DatabaseFieldType.DATE,
      value: value,
    };
  }

  private static processSelectValue(
    agent: DatabaseAgent,
    fieldId: string,
    value: string
  ): DatabaseValue {
    // it is possible `value` is a string of multiple tags like "tag1, tag2, tag3"
    const values = value.split(',').map((v) => v.trim());

    const optionIdsOrValues = [];
    for (const value of values) {
      const optionId = ValueProcessor.getOrCreateSelectOption(agent, fieldId, value);
      optionIdsOrValues.push(optionId ? optionId : value);
    }

    return {
      type: DatabaseFieldType.SELECT,
      value: optionIdsOrValues,
    };
  }

  private static processHyperlinkValue(value: string): DatabaseValue {
    const hyperlink: Hyperlink = {
      url: value,
      label: undefined,
      isLabelExplicit: false,
    };

    return {
      type: DatabaseFieldType.HYPERLINK,
      value: [
        {
          ...hyperlink,
          _id: generateId(),
        },
      ],
    };
  }

  private async processUserValueForFilter(accountIds: string[]): Promise<DatabaseValue> {
    const results = await this.userDataLoader.loadMany(accountIds);

    const errors: ProcessingError[] = [];
    const users: UserReference[] = [];

    for (const result of results) {
      if ('accountId' in result) {
        users.push(result);
      } else {
        errors.push(result);
      }
    }

    if (errors.length > 0) {
      this.logValidationErrors(errors);
    }

    return {
      type: DatabaseFieldType.USER,
      value: users.map((user) => {
        return {
          accountId: user.accountId,
          _id: generateId(),
        };
      }),
    };
  }

  private async processUserValue(
    accountIds: string[],
    entryId: string
  ): Promise<DatabaseValueChange> {
    const databaseValue = await this.processUserValueForFilter(accountIds);
    return {
      entryIdToChange: entryId,
      databaseValue,
    };
  }

  private async processPageLinkValue(
    value: string,
    entryId: string
  ): Promise<DatabaseValueChange> {
    const validationResult = await this.validatePageLink(value);

    if (
      validationResult.errors.length > 0 ||
      !validationResult.contentId ||
      !validationResult.ari
    ) {
      this.logValidationErrors(validationResult.errors);
    }

    return {
      entryIdToChange: entryId,
      databaseValue: {
        type: DatabaseFieldType.PAGE_LINK,
        value: [
          {
            ari: validationResult.ari,
            contentId: validationResult.contentId,
            _id: generateId(),
          },
        ],
      },
    };
  }

  private async validatePageLink(value: string): Promise<{
    errors: ProcessingError[];
    contentId?: string;
    ari?: string;
  }> {
    const errors: ProcessingError[] = [];
    const contentId = matchViewPageOrBlogpostPath(value)?.contentId;

    if (!contentId) {
      errors.push({
        message: `Content ID not found for page link value: ${value}`,
      });

      return { errors };
    }

    const content = await getContentById(contentId);
    if (!content) {
      errors.push({
        message: `Content not found for content ID: ${contentId}`,
      });

      return { errors };
    }

    return { errors, contentId: content.id, ari: content.ari };
  }

  private async processJiraIssueValue(
    fieldId: string,
    urls: string[],
    entryId: string
  ): Promise<DatabaseValueChange> {
    const errors: ProcessingError[] = [];

    // Retrieve cloudId and set it on field if successful
    let cloudId = this.agent.getCloudId(fieldId);
    const issueKeys = urls.map((url) => {
      const key = extractIssueKeyFromUrl(url);
      if (!cloudId) {
        // Gets first valid cloudId for now
        const newCloudId = getCloudId(url);
        if (newCloudId) {
          cloudId = newCloudId;
          this.agent.setCloudId(fieldId, cloudId);
        }
      }
      return key;
    });

    if (!cloudId) {
      this.logValidationErrors([{ message: "Couldn't retrieve Jira instance" }]);
      return {
        entryIdToChange: entryId,
        databaseValue: {
          type: DatabaseFieldType.JIRA_ISSUE,
          value: [],
        },
      };
    }

    let dataLoader = this.jiraIssueDataLoaders.get(fieldId);
    if (!dataLoader) {
      dataLoader = new JiraIssueDataLoader();
      this.jiraIssueDataLoaders.set(fieldId, dataLoader);
    }

    // Using cloudId, retrieve baseUrl to use for fetching jira issue
    if (!dataLoader.getJiraBaseUrl()) {
      const baseUrl = new JiraService().getTenantBaseUrl({ cloudId });
      dataLoader.setJiraBaseUrl(baseUrl);
    }

    const results = await dataLoader.loadMany(issueKeys);

    const issueKeyResults: string[] = [];
    for (const result of results) {
      if (typeof result === 'string') {
        issueKeyResults.push(result);
      } else {
        errors.push(result);
      }
    }

    if (errors.length > 0) {
      this.logValidationErrors(errors);
    }

    return {
      entryIdToChange: entryId,
      databaseValue: {
        type: DatabaseFieldType.JIRA_ISSUE,
        value: issueKeyResults.map((key) => {
          return {
            key,
            _id: generateId(),
          };
        }),
      },
    };
  }

  private static getOrCreateSelectOption(
    agent: DatabaseAgent,
    fieldId: string,
    optionValue: string
  ): string | undefined {
    const field = agent.getFieldById(fieldId);
    if (!field || field.type !== DatabaseFieldType.SELECT) {
      return;
    }

    const existingOption = field.settings.selectOptions?.find(
      (option) => option.id === optionValue || option.label === optionValue
    );

    if (existingOption) {
      return existingOption.id;
    }

    try {
      return agent.getOrCreateSelectOption(fieldId, optionValue);
    } catch (error) {
      throw new Error(
        `Failed to create select option ${optionValue} for field ${fieldId}: ${error}`
      );
    }
  }

  private logValidationErrors(errors: ProcessingError[]): void {
    for (const error of errors) {
      logError(new Error(error.message));
    }
  }
}
